import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { postBulkUserInvite } from '../../api/Terminal/UserAccount';
import type { IPostBulkUserInvitePayload } from '../../api/Terminal/UserAccount/types';
import { notifyUser } from '../../store/Toast';
import { csvFileToArray, readCSVFile } from '../../utils/csv';
import { emailRegEx } from '../../utils/RegularExpressions';
import { useTitleByName } from '../ReactQueryHooks/useTitlesQuery';

export interface IInvalidRecordsArray {
  row: number;
  value: string;
  col: string | number;
  reason: string;
  result: string;
}

export interface IValidRecordsArray extends IBulkUserInviteRecord {}

export default function useParseCSV() {
  const dispatch = useDispatch();

  const [invalidRecords, setInvalidRecords] = useState<IInvalidRecordsArray[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showBulkUserInviteSummary, setShowBulkUserInviteSummary] = useState(false);

  const { getTitle } = useTitleByName('');

  const uploadFile = useMutation((data: IPostBulkUserInvitePayload) => postBulkUserInvite({ data }));
  const batchId = uploadFile?.data?.batchId;

  async function parseFile(file: any) {
    const filename = file[0].name;
    const refType = 'Bulk_User_Invite';

    const validRecordsArray: IValidRecordsArray[] = [];
    const invalidRecordsArray: IInvalidRecordsArray[] = [];

    const fileRecordKeys = await readCSVFile(file[0]);
    const records = csvFileToArray(fileRecordKeys as string);
    setTotalRecords(records.length - 1);

    //Validating file columns
    const firstRow: string[] = (fileRecordKeys as string).split('\n')[0].replaceAll('\r', '').split(',');

    let validFile = true;

    //Missing Header row validation
    if (firstRow[0] !== 'Name' && firstRow[1] !== 'Organization Title' && firstRow[2] !== 'Email') {
      validFile = validFile && false;
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage:
            'Invalid or Missing header row: Name, Organization Title and Email header row is required . Please add header and try again.',
          toastType: 'failed',
        })
      );
      return;
    }

    //Missing Columns validation
    const requiredCols = ['Name', 'Organization Title', 'Email'];
    const missingCols: string[] = [];
    requiredCols.forEach((c) => {
      if (!firstRow.includes(c)) {
        validFile = validFile && false;
        missingCols.push(c);
      }
    });

    if (missingCols.length > 0) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `Missing Columns: ${missingCols.join(',')}. Please add missing columns and try again.`,
          toastType: 'failed',
        })
      );
      return;
    }

    //Invalid Columns arrangement validation
    if (firstRow.indexOf('Name') !== 0 || firstRow.indexOf('Organization Title') !== 1 || firstRow.indexOf('Email') !== 2) {
      validFile = validFile && false;
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage:
            'Invalid columns arrangement: Columns must be arranged in this order: Name, Organization Title, Email. Please arrange columns and try again.',
          toastType: 'failed',
        })
      );
      return;
    }

    //Validating all columns header to find extra columns
    const invalidCols: string[] = [];
    firstRow.forEach((col) => {
      if (col === 'Name' || col === 'Organization Title' || col === 'Email') {
        validFile = validFile && true;
      } else {
        validFile = validFile && false;
        invalidCols.push(col);
      }
    });

    if (invalidCols.length > 0) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `Invalid columns: ${invalidCols.join(',')}. Please remove these columns and try again.`,
          toastType: 'failed',
        })
      );
      return;
    }

    //Data Row records validation
    records.forEach((data: Record<string, any>, index: number) => {
      const organizationTitleId = getTitle(data['Organization Title'])?.id;

      let recordIsValid = true;
      Object.keys(data).forEach((k) => {
        //Empty field validation
        if (!data[k]) {
          invalidRecordsArray.push({
            row: index + 1,
            value: 'blank',
            col: k,
            reason: 'empty',
            result: 'failed',
          });
          recordIsValid = false;
        }

        //Organization Title Validation
        if (k === 'Organization Title' && data[k]) {
          if (!organizationTitleId) {
            recordIsValid = false;
            invalidRecordsArray.push({
              row: index + 1,
              value: `${data[k]}`,
              col: k,
              reason: 'invalid',
              result: 'failed',
            });
          }
        }

        //Email Validation
        if (k === 'Email' && data[k]) {
          const validEmail = emailRegEx.test(data[k]);
          if (!validEmail) {
            recordIsValid = false;
            invalidRecordsArray.push({
              row: index + 1,
              value: `${data[k]}`,
              col: k,
              reason: 'invalid',
              result: 'failed',
            });
          }
        }
      });

      if (recordIsValid) {
        validRecordsArray.push({
          titleId: organizationTitleId!,
          name: data?.Name,
          email: data?.email,
        });
      }
    });

    setInvalidRecords(invalidRecordsArray);
    setShowBulkUserInviteSummary(true);
    if (validRecordsArray.length > 0) {
      uploadFile.mutate({
        filename,
        refType,
        records: validRecordsArray,
      });
    }
  }

  return {
    uploadFile,
    parseFile,
    invalidRecords,
    totalRecords,
    batchId,
    showBulkUserInviteSummary,
    setShowBulkUserInviteSummary,
  };
}
